// Página Comandas/Mesas (Pedro, 30/10/2026; proposta escolhida: A, quadro de cartões). Só no Ultra, com o pedido na mesa
// ligado: o nome segue a chave (comanda individual → Comandas; pedido na mesa → Mesas). Tudo da conta acontece aqui:
// lançar o pedido («+ Pedido», com os produtos do PDV), conferir o consumo, mudar a etapa de cada pedido, imprimir e
// fechar a conta. O pedido só sai da conta no «Fechar conta» (conta_fechada_em); o «Só registrar pagamento» recebe e
// deixa na conta, marcado como pago. No fechamento, cada pedido a pagar tem a caixinha: dá para pagar um pedido só.
// O filtro «Fechadas» (Pedro, 01/11/2026, A-0065) lista as contas fechadas dos últimos 7 dias para imprimir de novo.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch, useGetStore, useListProducts, useListCategories } from "@workspace/api-client-react";
import type { Customer, Product } from "@workspace/api-client-react";
import { Check, ChevronDown, ChevronLeft, Loader2, Plus, Printer, ScanQrCode, Search, UtensilsCrossed, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useFeatureAllowed } from "@/hooks/use-plan";
import { usePdvCart } from "@/hooks/use-pdv-cart";
import { formatCurrency, orderDisplayCode } from "@/lib/utils";
import { extractApiErrorMessage } from "@/lib/api-error";
import { CartItemRow, NotifyCustomerDialog, ProductCard, ProductDetailDialog, STATUS_OPTIONS } from "@/components/pdv/PDVShared";
import { LerQrDaMesa } from "@/components/pdv/LerQrDaMesa";
import { CadastroNaComanda, imprimirNaJanela, tempoAberto } from "@/pages/admin/PDV";

interface ItemDoPedido {
  nome: string;
  quantidade: number;
  valor: number;
  opcoes: string[];
  obs: string | null;
}
interface Pedido {
  id: string;
  tableNumber: string;
  dailyOrderNumber: number | null;
  total: string;
  status: string;
  paymentMethod: string;
  createdAt: string;
  customerId: string | null;
  customerName: string | null;
  customerWhatsapp: string | null;
  source: string;
  deliveryFee: string | null;
  deliveryMode: string | null;
  taxaServico: string | null;
  local: string | null;
  itens: ItemDoPedido[];
}
interface Presa {
  mesa: string;
  customerId: string;
  desde: string;
  esperando: boolean;
  local: string | null;
  nome: string | null;
  whatsapp: string | null;
  whatsappNumber: string | null;
}
interface Pessoa {
  id: string;
  nome: string;
  pedidos: Pedido[];
}
interface Conta {
  chave: string;
  pedidos: Pedido[];
  presa: Presa | null;
}
interface ContaFechada {
  chave: string;
  mesa: string;
  quando: string;
  total: number;
  formas: string[];
  clientes: string[];
  whatsapps: string[];
  local: string | null;
  orderIds: string[];
}
type Filtro = "todas" | "esperando" | "pedido" | "paradas" | "fechadas";

const chave = (t: string | null | undefined) => String(t ?? "").trim().toLowerCase().replace(/^(mesa|comanda)\s*/i, "");
const valor = (p: Pedido) => Number(p.total) || 0;
const pago = (p: Pedido) => p.paymentMethod !== "Pendente";
const soma = (ps: Pedido[]) => Math.round(ps.reduce((s, p) => s + valor(p), 0) * 100) / 100;
const ROTULO_DA_ETAPA: Record<string, string> = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));
const ETAPAS = [
  { value: "em_preparo", label: "Em preparo" },
  { value: "pronto", label: "Pronto" },
  { value: "entregue", label: "Entregue" },
];
const buscarPedidos = () => customFetch<Pedido[]>("/api/orders/open-tabs/pagina");
const buscarPresas = () => customFetch<{ porCliente: boolean; contas: Presa[] }>("/api/orders/open-tabs/contas");
const buscarFechadas = () => customFetch<ContaFechada[]>("/api/orders/open-tabs/fechadas");
const juntarNomes = (nomes: string[]) => (nomes.length <= 1 ? nomes[0] ?? "" : `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`);

function pessoasDa(c: Conta): Pessoa[] {
  const m = new Map<string, Pessoa>();
  for (const p of c.pedidos) {
    const id = p.customerId ?? "sem";
    const x = m.get(id) ?? { id, nome: p.customerName || "Cliente", pedidos: [] };
    x.pedidos.push(p);
    m.set(id, x);
  }
  return [...m.values()];
}
function nomeDaConta(c: Conta, porCliente: boolean): string {
  if (porCliente) return c.presa?.nome || c.pedidos[c.pedidos.length - 1]?.customerName || "Sem nome";
  const nomes = [...new Set(c.pedidos.map((p) => p.customerName).filter(Boolean) as string[])];
  if (nomes.length === 0) return "Mesa";
  return juntarNomes(nomes);
}
function desdeDa(c: Conta): string | null {
  const datas = [...c.pedidos.map((p) => p.createdAt), ...(c.presa ? [c.presa.desde] : [])].sort();
  return datas[0] ?? null;
}

export default function Comandas() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: store } = useGetStore();
  const podeUsar = useFeatureAllowed("table_orders");
  const porCliente = (store as any)?.tableQrPerClient === true;
  const ligada = (store as any)?.tableOrdersEnabled === true;
  const nomeDaPagina = porCliente ? "Comandas" : "Mesas";
  const nomeDe = porCliente ? "Comanda" : "Mesa";
  const codeStyle = (store as any)?.orderCodeStyle ?? null;
  // «Mostrar comandas fechadas» em Configurações (ligado por padrão).
  const mostraFechadas = (store as any)?.tableShowClosed !== false;

  const pedidosQ = useQuery({ queryKey: ["comandas-pedidos"], queryFn: buscarPedidos, refetchInterval: 8000, enabled: podeUsar && ligada });
  const presasQ = useQuery({ queryKey: ["pdv-open-tabs-contas"], queryFn: buscarPresas, refetchInterval: 8000, enabled: podeUsar && ligada && porCliente });
  const atualizar = () => {
    qc.invalidateQueries({ queryKey: ["comandas-pedidos"] });
    qc.invalidateQueries({ queryKey: ["pdv-open-tabs-contas"] });
    qc.invalidateQueries({ queryKey: ["pdv-open-tabs"] });
    qc.invalidateQueries({ queryKey: ["comandas-fechadas"] });
  };

  const contas = useMemo(() => {
    const m = new Map<string, Conta>();
    const pegar = (n: string) => {
      const k = chave(n);
      const c = m.get(k) ?? { chave: k, pedidos: [], presa: null };
      m.set(k, c);
      return c;
    };
    for (const p of [...(pedidosQ.data ?? [])].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt))) pegar(p.tableNumber).pedidos.push(p);
    if (porCliente) for (const pr of presasQ.data?.contas ?? []) pegar(pr.mesa).presa = pr;
    return [...m.values()].sort((a, b) => (Number(a.chave) || 1e9) - (Number(b.chave) || 1e9) || a.chave.localeCompare(b.chave));
  }, [pedidosQ.data, presasQ.data, porCliente]);

  const [busca, setBusca] = useState("");
  const [filtroEscolhido, setFiltro] = useState<Filtro>("todas");
  const filtro: Filtro = filtroEscolhido === "fechadas" && !mostraFechadas ? "todas" : filtroEscolhido;
  const fechadasQ = useQuery({ queryKey: ["comandas-fechadas"], queryFn: buscarFechadas, refetchInterval: 15000, enabled: podeUsar && ligada && filtro === "fechadas" });
  const [aberta, setAberta] = useState<string | null>(null);
  const [pedindo, setPedindo] = useState<string | null>(null);
  const [fechando, setFechando] = useState<{ conta: string; pessoa: string | null } | null>(null);
  const [cadastro, setCadastro] = useState<{ mesa?: string } | null>(null);
  const [lendoQr, setLendoQr] = useState(false);

  const parada = (c: Conta) => {
    const d = desdeDa(c);
    return !!d && Date.now() - +new Date(d) > 12 * 3600 * 1000;
  };
  const contagem: Record<Filtro, number | undefined> = {
    todas: contas.length,
    esperando: contas.filter((c) => c.presa?.esperando).length,
    pedido: contas.filter((c) => c.pedidos.length > 0).length,
    paradas: contas.filter(parada).length,
    // Só se busca ao tocar no filtro: antes disso, sem número.
    fechadas: fechadasQ.data?.length,
  };
  const visiveis = contas.filter((c) => {
    if (filtro === "esperando" && !c.presa?.esperando) return false;
    if (filtro === "pedido" && c.pedidos.length === 0) return false;
    if (filtro === "paradas" && !parada(c)) return false;
    const q = busca.trim().toLowerCase();
    if (!q) return true;
    const digitos = q.replace(/\D/g, "");
    return (
      c.chave === chave(q) ||
      nomeDaConta(c, porCliente).toLowerCase().includes(q) ||
      (digitos.length >= 3 && [c.presa?.whatsapp, ...c.pedidos.map((p) => p.customerWhatsapp)].some((w) => (w ?? "").replace(/\D/g, "").includes(digitos)))
    );
  });
  const emAberto = soma(contas.flatMap((c) => c.pedidos.filter((p) => !pago(p))));

  const contaAberta = contas.find((c) => c.chave === aberta) ?? null;
  const contaDoPedido = contas.find((c) => c.chave === pedindo) ?? null;
  const contaDoFechamento = contas.find((c) => c.chave === fechando?.conta) ?? null;

  const lerQr = async (texto: string): Promise<string | null> => {
    let mesa: string;
    try {
      ({ mesa } = await customFetch<{ mesa: string }>("/api/orders/open-tabs/ler-qr", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ texto }) }));
    } catch (err) {
      return extractApiErrorMessage(err, "Não foi possível ler este QR code.");
    }
    setLendoQr(false);
    const k = chave(mesa);
    if (contas.some((c) => c.chave === k)) setAberta(k);
    else if (porCliente) setCadastro({ mesa });
    else toast({ title: `A mesa ${mesa} não tem conta aberta.` });
    return null;
  };

  if (!store) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!podeUsar || !ligada) {
    return (
      <div className="mx-auto max-w-md py-16 text-center" data-testid="comandas-indisponivel">
        <UtensilsCrossed className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 font-semibold">{podeUsar ? "Ligue o pedido na mesa em Configurações." : "Comandas e Mesas são do plano Ultra."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="comandas">
      <div className="flex flex-wrap items-center gap-3">
        <UtensilsCrossed className="h-6 w-6 text-primary" />
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold">{nomeDaPagina}</h1>
          <p className="text-xs text-muted-foreground">{contas.length} {contas.length === 1 ? "aberta" : "abertas"} · {formatCurrency(emAberto)}</p>
        </div>
        <div className="grid w-full grid-cols-2 gap-2 md:ml-auto md:flex md:w-auto md:items-center">
          <div className="relative col-span-2 md:w-64">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Número, nome ou telefone" className="pl-8" data-testid="comandas-busca" />
          </div>
          {porCliente && (
            <Button variant="outline" onClick={() => setCadastro({})} data-testid="comandas-nova">
              <Plus className="mr-1 h-4 w-4" /> Comanda
            </Button>
          )}
          <Button variant="outline" className={porCliente ? "" : "col-span-2"} onClick={() => setLendoQr(true)} data-testid="comandas-ler-qr">
            <ScanQrCode className="mr-1 h-4 w-4" /> Ler QR code
          </Button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar text-xs" role="group" aria-label="Filtrar">
        {([["todas", "Todas"], ...(porCliente ? [["esperando", "Aguardando celular"]] : []), ["pedido", "Com pedido"], ["paradas", "Paradas"], ...(mostraFechadas ? [["fechadas", "Fechadas"]] : [])] as Array<[Filtro, string]>).map(([f, rotulo]) => (
          <button
            key={f}
            type="button"
            onClick={() => setFiltro(f)}
            aria-pressed={filtro === f}
            className={`shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 font-medium ${filtro === f ? "border-foreground bg-foreground text-background" : "bg-background text-muted-foreground hover:bg-muted"}`}
            data-testid={`comandas-filtro-${f}`}
          >
            {rotulo}{contagem[f] === undefined ? "" : ` ${contagem[f]}`}
          </button>
        ))}
      </div>

      {filtro === "fechadas" ? (
        <Fechadas q={fechadasQ} busca={busca} porCliente={porCliente} nomeDe={nomeDe} />
      ) : (
        <>
          {pedidosQ.isError && <p className="text-sm text-destructive">{extractApiErrorMessage(pedidosQ.error, "Não foi possível carregar as contas.")}</p>}
          {pedidosQ.isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : visiveis.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground" data-testid="comandas-vazio">{contas.length ? "Nada com esse filtro." : `Nenhuma ${nomeDe.toLowerCase()} aberta.`}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-3">
              {visiveis.map((c) => (
                <Cartao key={c.chave} c={c} porCliente={porCliente} nomeDe={nomeDe} parada={parada(c)} onAbrir={() => setAberta(c.chave)} onPedido={() => setPedindo(c.chave)} onFechar={() => setFechando({ conta: c.chave, pessoa: null })} />
              ))}
            </div>
          )}
        </>
      )}

      {contaAberta && (
        <Gaveta
          c={contaAberta}
          porCliente={porCliente}
          nomeDe={nomeDe}
          codeStyle={codeStyle}
          onFechar={() => setAberta(null)}
          onPedido={() => setPedindo(contaAberta.chave)}
          onFecharConta={(pessoa) => setFechando({ conta: contaAberta.chave, pessoa })}
          onMudou={atualizar}
        />
      )}
      {contaDoPedido && <PedidoNovo c={contaDoPedido} porCliente={porCliente} nomeDe={nomeDe} slug={(user as any)?.store?.slug || (store as any)?.slug || ""} store={store} onFechar={() => setPedindo(null)} onLancou={atualizar} />}
      {contaDoFechamento && fechando && (
        <Fechamento
          c={contaDoFechamento}
          pessoa={fechando.pessoa}
          porCliente={porCliente}
          nomeDe={nomeDe}
          store={store}
          codeStyle={codeStyle}
          onFechar={() => setFechando(null)}
          onMudou={atualizar}
          onPronto={(fechouTudo) => {
            setFechando(null);
            if (fechouTudo) setAberta(null);
            atualizar();
          }}
        />
      )}
      {lendoQr && <LerQrDaMesa onLido={lerQr} onClose={() => setLendoQr(false)} porCliente={porCliente} />}
      {cadastro && (
        <CadastroNaComanda
          mesaLida={cadastro.mesa}
          onClose={() => {
            setCadastro(null);
            atualizar();
          }}
        />
      )}
    </div>
  );
}

// «Fechadas»: as contas fechadas pela página nos últimos 7 dias, a mais nova primeiro, para imprimir de novo (a 2ª via sai
// com a taxa de serviço, o pagamento e a hora do fechamento).
function Fechadas({ q, busca, porCliente, nomeDe }: { q: { data?: ContaFechada[]; isError: boolean; error: unknown }; busca: string; porCliente: boolean; nomeDe: string }) {
  const erro = q.isError && <p className="text-sm text-destructive">{extractApiErrorMessage(q.error, "Não foi possível carregar as contas fechadas.")}</p>;
  if (!q.data) return erro || <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  const termo = busca.trim().toLowerCase();
  const digitos = termo.replace(/\D/g, "");
  const visiveis = q.data.filter(
    (c) =>
      !termo ||
      c.mesa === chave(termo) ||
      c.clientes.some((n) => n.toLowerCase().includes(termo)) ||
      (digitos.length >= 3 && c.whatsapps.some((w) => w.replace(/\D/g, "").includes(digitos))),
  );
  if (visiveis.length === 0) {
    return (
      <>
        {erro}
        <p className="py-12 text-center text-sm text-muted-foreground" data-testid="comandas-fechadas-vazio">
          {q.data.length ? "Nada com essa busca." : `Nenhuma ${nomeDe.toLowerCase()} fechada nos últimos 7 dias.`}
        </p>
      </>
    );
  }
  return (
    <div className="space-y-2" data-testid="comandas-fechadas">
      {erro}
      <p className="text-xs text-muted-foreground">Fechadas nos últimos 7 dias</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-3">
        {visiveis.map((c) => (
          <CartaoFechado key={c.chave} c={c} porCliente={porCliente} nomeDe={nomeDe} />
        ))}
      </div>
    </div>
  );
}

function CartaoFechado({ c, porCliente, nomeDe }: { c: ContaFechada; porCliente: boolean; nomeDe: string }) {
  const { toast } = useToast();
  const [imprimindo, setImprimindo] = useState(false);
  const imprimir = async () => {
    setImprimindo(true);
    try {
      const r = await customFetch<{ via: string; texto?: string }>("/api/orders/open-tabs/imprimir-conta-fechada", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ orderIds: c.orderIds }) });
      if (r.via === "janela" && r.texto) imprimirNaJanela(r.texto);
      else toast({ title: "Conta enviada para a impressora" });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível imprimir a conta."), variant: "destructive" });
    } finally {
      setImprimindo(false);
    }
  };
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border bg-card" data-testid={`comandas-fechada-${c.mesa}`}>
      <span className="h-1 bg-muted-foreground/30" />
      <div className="flex-1 p-3">
        <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{nomeDe}</div>
        <div className="font-display text-2xl font-bold leading-none">{c.mesa}</div>
        <div className="mt-1.5 truncate text-sm font-semibold">{juntarNomes(c.clientes) || (porCliente ? "Sem nome" : "Mesa")}</div>
        <div className="truncate text-[11px] text-muted-foreground">{[`Fechada ${c.quando}`, c.local ? `Mesa ${c.local}` : null].filter(Boolean).join(" · ")}</div>
        <div className="mt-2 text-sm font-semibold tabular-nums">
          {formatCurrency(c.total)} {c.formas.length > 0 && <span className="text-[10px] font-normal text-muted-foreground">· {c.formas.join(" + ")}</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={() => void imprimir()}
        disabled={imprimindo}
        className="flex items-center justify-center gap-1 border-t py-2 text-[11px] font-semibold hover:bg-muted/40 disabled:opacity-60"
        data-testid={`comandas-reimprimir-${c.mesa}`}
      >
        {imprimindo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Printer className="h-3 w-3" />} Imprimir de novo
      </button>
    </div>
  );
}

const SELO_PAGO = <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">pago</span>;

function Cartao({ c, porCliente, nomeDe, parada, onAbrir, onPedido, onFechar }: { c: Conta; porCliente: boolean; nomeDe: string; parada: boolean; onAbrir: () => void; onPedido: () => void; onFechar: () => void }) {
  const desde = desdeDa(c);
  const tempo = desde ? tempoAberto(desde).texto : null;
  const pagos = c.pedidos.filter(pago);
  const falta = soma(c.pedidos.filter((p) => !pago(p)));
  const tudoPago = c.pedidos.length > 0 && pagos.length === c.pedidos.length;
  const parcial = pagos.length > 0 && !tudoPago;
  const cor = c.presa?.esperando ? "bg-amber-400" : parada ? "bg-red-900" : "bg-primary";
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border bg-card" data-testid={`comandas-cartao-${c.chave}`}>
      <span className={`h-1 ${cor}`} />
      <button type="button" onClick={onAbrir} className="flex-1 p-3 text-left hover:bg-muted/40" data-testid={`comandas-abrir-${c.chave}`}>
        <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{nomeDe}</div>
        <div className="font-display text-2xl font-bold leading-none">{c.chave}</div>
        <div className="mt-1.5 truncate text-sm font-semibold">{nomeDaConta(c, porCliente)}</div>
        <div className="truncate text-[11px] text-muted-foreground">{[c.presa?.local ? `Mesa ${c.presa.local}` : null, tempo].filter(Boolean).join(" · ") || "sem pedido"}</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {c.presa?.esperando && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">aguardando o celular</span>}
          {parada && <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800">parada há +12h</span>}
          {tudoPago && SELO_PAGO}
          {parcial && <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800" data-testid={`comandas-parcial-${c.chave}`}>pagamento parcial</span>}
        </div>
        <div className="mt-2 text-sm font-semibold tabular-nums">
          {parcial ? (
            <>
              {formatCurrency(falta)} <span className="text-[10px] font-normal text-muted-foreground">falta · de {formatCurrency(soma(c.pedidos))}</span>
            </>
          ) : (
            <>
              {formatCurrency(soma(c.pedidos))}{" "}
              <span className="text-[10px] font-normal text-muted-foreground">· {c.pedidos.length ? `${c.pedidos.length} ${c.pedidos.length === 1 ? "pedido" : "pedidos"}` : "sem pedido"}</span>
            </>
          )}
        </div>
      </button>
      <div className="grid grid-cols-2 border-t text-[11px] font-semibold">
        <button type="button" onClick={onPedido} className="flex items-center justify-center gap-0.5 py-2 hover:bg-muted/40" data-testid={`comandas-pedido-${c.chave}`}>
          <Plus className="h-3 w-3" /> Pedido
        </button>
        <button type="button" onClick={onFechar} className="border-l py-2 text-destructive hover:bg-muted/40" data-testid={`comandas-fechar-${c.chave}`}>
          Fechar<span className="hidden md:inline"> conta</span>
        </button>
      </div>
    </div>
  );
}

// O selo da etapa: tocar abre Em preparo, Pronto e Entregue ali mesmo (muda o pedido de verdade, também em Pedidos). E
// «Cancelar pedido», com confirmação (lojista da comanda individual e Pedro, 31/10/2026, A-0061: «Até achei que já
// tinha»): o cancelado sai da comanda, do total e do fechamento, como quando se cancela em Pedidos.
function SeloDaEtapa({ p, codeStyle, onMudou }: { p: Pedido; codeStyle: string | null; onMudou: () => void }) {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [indo, setIndo] = useState(false);
  const cancelar = async () => {
    const ok = await confirm({
      title: `Cancelar o pedido #${orderDisplayCode(p as any, codeStyle)}?`,
      description: `Ele sai da comanda e do total (${formatCurrency(valor(p))}). O cliente não recebe mensagem.`,
      confirmLabel: "Cancelar pedido",
      cancelLabel: "Voltar",
    });
    if (ok) await mudar("cancelado");
  };
  const mudar = async (status: string) => {
    if (status === p.status) return;
    setIndo(true);
    try {
      await customFetch(`/api/orders/${p.id}/status`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status, notifyCustomer: false }) });
      onMudou();
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível mudar a etapa."), variant: "destructive" });
    } finally {
      setIndo(false);
    }
  };
  const entregue = p.status === "entregue";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={indo}
          className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${entregue ? "bg-emerald-50 text-emerald-700" : "bg-amber-100 text-amber-800"}`}
          data-testid={`comandas-etapa-${p.id}`}
        >
          {indo ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : entregue ? "✓ entregue" : (ROTULO_DA_ETAPA[p.status] ?? p.status).toLowerCase()}
          <ChevronDown className="h-2.5 w-2.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-40">
        {ETAPAS.map((e) => (
          <DropdownMenuItem key={e.value} onSelect={() => void mudar(e.value)} data-testid={`comandas-etapa-${p.id}-${e.value}`}>
            <span className="flex-1">{e.label}</span>
            {p.status === e.value && <Check />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void cancelar()} className="text-destructive focus:text-destructive" data-testid={`comandas-etapa-${p.id}-cancelar`}>
          Cancelar pedido
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PedidoDaConta({ p, codeStyle, onMudou, caixa, apagado = false }: { p: Pedido; codeStyle: string | null; onMudou: () => void; caixa?: ReactNode; apagado?: boolean }) {
  return (
    <div className={`flex items-start gap-3 rounded-xl border bg-card p-2.5 ${apagado ? "opacity-60" : ""}`} data-testid={`comandas-pedido-da-conta-${p.id}`}>
      {caixa}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span className="font-mono">#{orderDisplayCode(p as any, codeStyle)}</span>
          <span className="rounded bg-muted px-1 font-semibold">{p.source === "link" ? "QR" : "PDV"}</span>
          <span>{new Date(p.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
          <SeloDaEtapa p={p} codeStyle={codeStyle} onMudou={onMudou} />
          {pago(p) && SELO_PAGO}
          <span className="ml-auto font-semibold text-foreground">{formatCurrency(valor(p))}</span>
        </div>
        {p.itens.map((i, n) => (
          <div key={n} className="flex justify-between gap-2 text-sm">
            <span className="min-w-0">
              {i.quantidade}× {i.nome}
              {i.opcoes.length > 0 && <span className="block text-xs text-muted-foreground">{i.opcoes.join(", ")}</span>}
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{formatCurrency(i.valor)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Resumo({ pedidos }: { pedidos: Pedido[] }) {
  const pagos = pedidos.filter(pago);
  if (pagos.length === 0) {
    return (
      <div className="flex items-center justify-between rounded-xl border bg-card p-3" data-testid="comandas-resumo">
        <span className="text-sm text-muted-foreground">Consumo · {pedidos.length} {pedidos.length === 1 ? "pedido" : "pedidos"}</span>
        <span className="font-display text-lg font-bold">{formatCurrency(soma(pedidos))}</span>
      </div>
    );
  }
  const formas = [...new Set(pagos.map((p) => p.paymentMethod))].join(", ");
  return (
    <div className="space-y-1.5 rounded-xl border bg-card p-3 text-sm" data-testid="comandas-resumo">
      <div className="flex justify-between"><span className="text-muted-foreground">Consumo · {pedidos.length} {pedidos.length === 1 ? "pedido" : "pedidos"}</span><span>{formatCurrency(soma(pedidos))}</span></div>
      <div className="flex justify-between text-emerald-700 dark:text-emerald-400"><span>Pago · {formas}</span><span>{formatCurrency(soma(pagos))}</span></div>
      <div className="flex justify-between border-t pt-1.5 font-display text-base font-bold"><span>Falta pagar</span><span>{formatCurrency(soma(pedidos.filter((p) => !pago(p))))}</span></div>
    </div>
  );
}

// Gaveta da conta: à direita no computador, de baixo no celular.
function Gaveta({ c, porCliente, nomeDe, codeStyle, onFechar, onPedido, onFecharConta, onMudou }: { c: Conta; porCliente: boolean; nomeDe: string; codeStyle: string | null; onFechar: () => void; onPedido: () => void; onFecharConta: (pessoa: string | null) => void; onMudou: () => void }) {
  const { toast } = useToast();
  const [imprimindo, setImprimindo] = useState(false);
  const imprimir = async () => {
    const abertos = c.pedidos.filter((p) => !pago(p));
    if (abertos.length === 0) {
      toast({ title: "Não há pedido a pagar nesta conta." });
      return;
    }
    setImprimindo(true);
    try {
      const r = await customFetch<{ via: string; texto?: string }>("/api/orders/open-tabs/imprimir-conta", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ orderIds: abertos.map((p) => p.id), escopo: "mesa" }) });
      if (r.via === "janela" && r.texto) imprimirNaJanela(r.texto);
      else toast({ title: "Conta enviada para a impressora" });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível imprimir a conta."), variant: "destructive" });
    } finally {
      setImprimindo(false);
    }
  };
  const pessoas = porCliente ? [] : pessoasDa(c);
  return (
    <div className="fixed inset-0 z-40" data-testid="comandas-gaveta">
      <div className="absolute inset-0 bg-black/35 animate-in fade-in" onClick={onFechar} />
      <div className="absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col gap-3 overflow-y-auto rounded-t-2xl bg-background p-4 shadow-2xl animate-in slide-in-from-bottom md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[420px] md:rounded-none md:border-l md:slide-in-from-right md:slide-in-from-bottom-0">
        <div className="mx-auto h-1 w-10 shrink-0 rounded-full bg-muted md:hidden" />
        <div className="flex items-start gap-3">
          <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <span className="text-[9px] font-semibold uppercase tracking-wide">{nomeDe}</span>
            <span className="font-display text-2xl font-bold leading-none">{c.chave}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-lg font-bold">{nomeDaConta(c, porCliente)}</div>
            <div className="text-xs text-muted-foreground">{[c.presa?.local ? `Mesa ${c.presa.local}` : null, desdeDa(c) ? `aberta ${tempoAberto(desdeDa(c)!).texto}` : null, `${c.pedidos.length} ${c.pedidos.length === 1 ? "pedido" : "pedidos"}`].filter(Boolean).join(" · ")}</div>
          </div>
          <button type="button" onClick={onFechar} className="flex h-8 w-8 items-center justify-center rounded-lg border text-muted-foreground" aria-label="Fechar"><X className="h-4 w-4" /></button>
        </div>
        {c.pedidos.length === 0 && <p className="rounded-xl border bg-card p-3 text-sm text-muted-foreground">{c.presa?.esperando ? "Aguardando o celular do cliente." : "Sem pedido."}</p>}
        {porCliente
          ? c.pedidos.map((p) => <PedidoDaConta key={p.id} p={p} codeStyle={codeStyle} onMudou={onMudou} />)
          : pessoas.map((x) => {
              const quitada = x.pedidos.every(pago);
              return (
                <div key={x.id} className="rounded-xl border bg-card" data-testid={`comandas-pessoa-${x.id}`}>
                  <div className="flex items-center gap-2 border-b px-3 py-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">{x.nome[0]}</span>
                    <span className="text-sm font-semibold">{x.nome}</span>
                    <span className="text-xs text-muted-foreground">{x.pedidos.length} {x.pedidos.length === 1 ? "pedido" : "pedidos"}</span>
                    <span className="ml-auto text-sm font-semibold tabular-nums">{formatCurrency(soma(x.pedidos))}</span>
                    {quitada ? SELO_PAGO : (
                      <button type="button" onClick={() => onFecharConta(x.id)} className="rounded-md border border-primary px-2 py-0.5 text-xs font-semibold text-primary" data-testid={`comandas-fechar-pessoa-${x.id}`}>
                        Fechar
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 p-2">{x.pedidos.map((p) => <PedidoDaConta key={p.id} p={p} codeStyle={codeStyle} onMudou={onMudou} />)}</div>
                </div>
              );
            })}
        {c.pedidos.length > 0 && <Resumo pedidos={c.pedidos} />}
        <div className="mt-auto space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={onPedido} className="bg-foreground text-background hover:bg-foreground/90" data-testid="comandas-gaveta-pedido"><Plus className="mr-1 h-4 w-4" /> Pedido</Button>
            <Button variant="outline" onClick={() => void imprimir()} disabled={imprimindo} data-testid="comandas-gaveta-imprimir">
              {imprimindo ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Printer className="mr-1 h-4 w-4" />} Imprimir conta
            </Button>
          </div>
          <Button className="w-full" onClick={() => onFecharConta(null)} data-testid="comandas-gaveta-fechar">{porCliente ? "Fechar conta" : "Fechar conta da mesa"}</Button>
        </div>
      </div>
    </div>
  );
}

// «+ Pedido»: os produtos do PDV na própria página; «Lançar na comanda» grava o pedido nela (a pagar na conta).
function PedidoNovo({ c, porCliente, nomeDe, slug, store, onFechar, onLancou }: { c: Conta; porCliente: boolean; nomeDe: string; slug: string; store: any; onFechar: () => void; onLancou: () => void }) {
  const { toast } = useToast();
  const { data: produtos = [] } = useListProducts();
  const { data: categorias = [] } = useListCategories();
  const cart = usePdvCart({ store, products: produtos as Product[], slug, ordersEndpoint: "/api/orders", source: "pdv", matchBarcode: false, naConta: true });
  const [pronto, setPronto] = useState(false);
  useEffect(() => {
    cart.setTableNumber(c.chave);
    cart.setPayLater(true);
    // A comanda individual é de uma pessoa: o pedido vai no nome dela, a do QR ou, sem o QR preso, a dos pedidos da
    // comanda (senão a conta ganhava um «Cliente» a mais).
    const doPedido = [...c.pedidos].reverse().find((p) => p.customerId);
    const dono = !porCliente
      ? null
      : c.presa
        ? { id: c.presa.customerId, nome: c.presa.nome, whats: c.presa.whatsappNumber }
        : doPedido
          ? { id: doPedido.customerId!, nome: doPedido.customerName, whats: doPedido.customerWhatsapp }
          : null;
    if (dono) cart.setCustomer({ kind: "existing", customer: { id: dono.id, firstName: dono.nome || "Cliente", whatsappNumber: (dono.whats ?? "").replace(/\D/g, ""), createdAt: "" } as Customer });
    setPronto(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.chave]);
  useEffect(() => {
    if (!cart.success) return;
    toast({ title: `Pedido lançado na ${nomeDe.toLowerCase()} ${c.chave}` });
    cart.handleNewSale();
    onLancou();
    onFechar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.success]);
  // A lista do PDV: o indisponível no link aparece (o ingresso que só a casa vende), a não ser com «Esconder produtos
  // indisponíveis no PDV» ligado em Configurações.
  const ativos = cart.filteredProducts as Product[];
  return (
    <div className="fixed inset-0 z-50" data-testid="comandas-pedido-novo">
      <div className="absolute inset-0 bg-black/35" onClick={onFechar} />
      <div className="absolute inset-0 flex flex-col bg-background shadow-2xl animate-in slide-in-from-right md:left-auto md:w-[860px] md:flex-row md:border-l">
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
          <button type="button" onClick={onFechar} className="flex items-center gap-1 self-start text-sm text-muted-foreground" data-testid="comandas-pedido-voltar">
            <ChevronLeft className="h-4 w-4" /> {nomeDe} {c.chave} · {nomeDaConta(c, porCliente)}
          </button>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={cart.search} onChange={(e) => cart.setSearch(e.target.value)} placeholder="Buscar produto" className="pl-8" data-testid="comandas-pedido-busca" />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 hide-scrollbar text-xs">
            {[{ id: "all", name: "Todos" }, ...(categorias as any[])].map((k: any) => (
              <button key={k.id} type="button" onClick={() => cart.setActiveCategory(k.id)} className={`shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 font-medium ${cart.activeCategory === k.id ? "border-primary bg-primary text-primary-foreground" : "bg-background text-muted-foreground"}`}>
                {k.name}
              </button>
            ))}
          </div>
          {/* Uma coluna, como no PDV: o cartão do produto é deitado (foto, nome, preço e o +) e não cabe em grade. */}
          <div className="space-y-2">
            {ativos.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                onClick={() => cart.setDetailProduct(p)}
                qtyInCart={cart.getCartItemQty(p.id)}
                onQuickAdd={() => cart.handleQuickAdd(p)}
                onQuickRemove={() => cart.handleQuickRemove(p)}
                onQuickIncrement={() => cart.handleQuickIncrement(p)}
              />
            ))}
          </div>
        </div>
        <div className="flex max-h-[45vh] shrink-0 flex-col gap-3 border-t bg-card p-4 md:max-h-none md:w-[300px] md:border-l md:border-t-0">
          <div className="font-display text-base font-bold">Pedido novo</div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
            {cart.cart.length === 0 ? <p className="text-sm text-muted-foreground">Escolha os produtos.</p> : cart.cart.map((it: any) => (
              <CartItemRow key={it.cartItemId} item={it} onRemove={() => cart.handleRemoveItem(it.cartItemId)} onQtyChange={(q) => cart.handleQtyChange(it.cartItemId, q)} onEdit={() => { cart.setEditingItem(it); cart.setDetailProduct(it.product); }} />
            ))}
          </div>
          <Button className="w-full" disabled={!pronto || cart.cart.length === 0 || cart.submitting} onClick={() => void cart.handleFinalize()} data-testid="comandas-pedido-lancar">
            {cart.submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Lançar na {nomeDe.toLowerCase()} {c.chave} · {formatCurrency(cart.subtotal)}
          </Button>
        </div>
      </div>
      {cart.detailProduct && (
        <ProductDetailDialog
          product={cart.detailProduct}
          editingItem={cart.editingItem}
          onClose={() => { cart.setDetailProduct(null); cart.setEditingItem(null); }}
          onConfirm={cart.handleAddOrUpdateItem}
          remainingKg={cart.remainingKg(cart.detailProduct, cart.editingItem?.cartItemId)}
          slug={slug}
          dataUnica={cart.dataUnica}
          horarios={cart.horariosDosTurnos}
          canalDaPromo={cart.canalDaPromo}
        />
      )}
      {cart.notifyPrompt && (
        <NotifyCustomerDialog message={cart.notifyPrompt.message} onMessageChange={(m) => cart.setNotifyPrompt({ ...cart.notifyPrompt!, message: m })} onSend={cart.sendNotify} onSkip={cart.dismissNotify} />
      )}
    </div>
  );
}

// «Fechar conta»: os pedidos (os a pagar com a caixinha), a taxa de serviço, a forma de pagamento, «Fechar conta» e
// «Só registrar pagamento». Na mesa, o «Fechar» de uma pessoa abre só os pedidos dela.
function Fechamento({ c, pessoa, porCliente, nomeDe, store, codeStyle, onFechar, onPronto, onMudou }: { c: Conta; pessoa: string | null; porCliente: boolean; nomeDe: string; store: any; codeStyle: string | null; onFechar: () => void; onPronto: (fechouTudo: boolean) => void; onMudou: () => void }) {
  const { toast } = useToast();
  const daPessoa = pessoa ? pessoasDa(c).find((x) => x.id === pessoa) ?? null : null;
  const pedidos = daPessoa ? daPessoa.pedidos : c.pedidos;
  const aPagar = pedidos.filter((p) => !pago(p));
  const [marcados, setMarcados] = useState<Set<string>>(() => new Set(aPagar.map((p) => p.id)));
  const taxaDaLoja = store?.pdvSettings?.taxaServico as { ativo?: boolean; percentual?: number } | undefined;
  const pct = taxaDaLoja?.ativo ? Number(taxaDaLoja.percentual) || 0 : 0;
  const [cobrarTaxa, setCobrarTaxa] = useState(pct > 0);
  const formas: string[] = Array.isArray(store?.acceptedPayments) && store.acceptedPayments.length ? store.acceptedPayments : ["Dinheiro", "PIX"];
  const [forma, setForma] = useState<string | null>(null);
  const [enviando, setEnviando] = useState<"fechar" | "registrar" | null>(null);
  const [imprimindo, setImprimindo] = useState(false);

  const selecionados = aPagar.filter((p) => marcados.has(p.id));
  const consumo = soma(selecionados);
  const base = selecionados.filter((p) => p.deliveryMode !== "entrega").reduce((s, p) => s + valor(p) - (Number(p.deliveryFee) || 0), 0);
  const taxa = cobrarTaxa && pct > 0 ? Math.round(base * pct) / 100 : 0;
  const total = Math.round((consumo + taxa) * 100) / 100;
  const todosMarcados = selecionados.length === aPagar.length;
  const precisaForma = selecionados.length > 0;
  const titulo = daPessoa ? `Fechar conta da ${daPessoa.nome}` : "Fechar conta";

  const enviar = async (modo: "fechar" | "registrar") => {
    if (precisaForma && !forma) {
      toast({ title: "Escolha a forma de pagamento.", variant: "destructive" });
      return;
    }
    if (modo === "registrar" && selecionados.length === 0) {
      toast({ title: "Marque o pedido a pagar." });
      return;
    }
    setEnviando(modo);
    try {
      // «Fechar conta» com tudo marcado fecha a conta (ou a parte da pessoa): o que falta pagar, o que já foi pago e o
      // que falta entregar. Com parte desmarcada, recebe e entrega os marcados; a conta continua com o resto.
      const fechaTudo = modo === "fechar" && todosMarcados;
      const ids = fechaTudo ? pedidos.map((p) => p.id) : selecionados.map((p) => p.id);
      // «Imprimir a conta ao fechar» ligado em Configurações: a resposta diz por onde a conta saiu.
      let impressao: { via: string; texto?: string } | undefined;
      if (ids.length > 0) {
        ({ impressao } = await customFetch<{ impressao?: { via: string; texto?: string } }>("/api/orders/open-tabs/close", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderIds: ids, ...(precisaForma ? { paymentMethod: forma } : {}), cobrarTaxaServico: cobrarTaxa && pct > 0, modo: fechaTudo ? "fechar" : "registrar" }),
        }));
      }
      const imprimiu = !impressao || impressao.via === "fila" || (impressao.via === "janela" && !!impressao.texto && imprimirNaJanela(impressao.texto));
      if (modo === "fechar" && !fechaTudo) {
        for (const p of selecionados) {
          if (p.status !== "entregue") await customFetch(`/api/orders/${p.id}/status`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: "entregue", notifyCustomer: false }) });
        }
      }
      // Comanda sem pedido: «Fechar conta» solta o número.
      if (fechaTudo && pedidos.length === 0 && c.presa) {
        await customFetch("/api/orders/open-tabs/liberar", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mesa: c.chave, customerId: c.presa.customerId }) });
      }
      toast({ title: modo === "registrar" ? "Pagamento registrado" : fechaTudo ? `Conta ${daPessoa ? `da ${daPessoa.nome} ` : `da ${nomeDe.toLowerCase()} ${c.chave} `}fechada` : "Pedidos recebidos e entregues", description: precisaForma ? `${selecionados.length} ${selecionados.length === 1 ? "pedido" : "pedidos"} · ${formatCurrency(total)} em ${forma}` : undefined });
      // Depois do «fechada»: aparece um aviso por vez, e este é o que importa.
      if (!imprimiu) toast({ title: "A conta fechou, mas não foi para a impressora", description: "Imprima de novo pelo filtro «Fechadas».", variant: "destructive" });
      onPronto(fechaTudo && !daPessoa);
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível fechar a conta."), variant: "destructive" });
    } finally {
      setEnviando(null);
    }
  };
  const imprimir = async () => {
    if (aPagar.length === 0) {
      toast({ title: "Não há pedido a pagar nesta conta." });
      return;
    }
    setImprimindo(true);
    try {
      const r = await customFetch<{ via: string; texto?: string }>("/api/orders/open-tabs/imprimir-conta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderIds: aPagar.map((p) => p.id), escopo: daPessoa ? "cliente" : "mesa", cobrarTaxaServico: cobrarTaxa && pct > 0 }),
      });
      if (r.via === "janela" && r.texto) imprimirNaJanela(r.texto);
      else toast({ title: "Conta enviada para a impressora" });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível imprimir a conta."), variant: "destructive" });
    } finally {
      setImprimindo(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="comandas-fechamento">
      <div className="absolute inset-0 bg-black/35" onClick={onFechar} />
      <div className="absolute inset-0 flex flex-col gap-4 overflow-y-auto bg-background p-4 shadow-2xl animate-in slide-in-from-right md:left-auto md:w-[760px] md:border-l md:p-6">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onFechar} className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="comandas-fechamento-voltar">
            <ChevronLeft className="h-4 w-4" /> {nomeDe} {c.chave} · {nomeDaConta(c, porCliente)}
          </button>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => void imprimir()} disabled={imprimindo} data-testid="comandas-fechamento-imprimir">
            {imprimindo ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Printer className="mr-1 h-3.5 w-3.5" />} Imprimir conta
          </Button>
        </div>
        <div>
          <div className="font-display text-xl font-bold">{titulo}</div>
          <div className="text-xs text-muted-foreground">{nomeDe} {c.chave} · {pedidos.length} {pedidos.length === 1 ? "pedido" : "pedidos"}</div>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            {pedidos.length === 0 && <p className="rounded-xl border bg-card p-3 text-sm text-muted-foreground">Sem pedido: «Fechar conta» deixa o número livre.</p>}
            {pedidos.map((p) => {
              const ehAPagar = !pago(p);
              const marcado = marcados.has(p.id);
              const caixa = ehAPagar ? (
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={marcado}
                  onClick={() => setMarcados((m) => { const n = new Set(m); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; })}
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${marcado ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40 bg-background"}`}
                  data-testid={`comandas-marcar-${p.id}`}
                >
                  {marcado && <Check className="h-3.5 w-3.5" />}
                </button>
              ) : undefined;
              return <PedidoDaConta key={p.id} p={p} codeStyle={codeStyle} onMudou={onMudou} caixa={caixa} apagado={ehAPagar && !marcado} />;
            })}
          </div>
          <div className="space-y-3">
            <div className="space-y-2 rounded-xl border bg-card p-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Consumo{selecionados.length !== aPagar.length ? ` · ${selecionados.length} de ${aPagar.length} pedidos` : ""}</span><span>{formatCurrency(consumo)}</span></div>
              {pct > 0 && (
                <div className="flex items-center gap-2">
                  <span>Cobrar taxa de serviço ({String(pct).replace(".", ",")}%)</span>
                  <Switch checked={cobrarTaxa} onCheckedChange={setCobrarTaxa} data-testid="comandas-fechamento-taxa" />
                  <span className="ml-auto">{formatCurrency(taxa)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 font-display text-lg font-bold"><span>Total</span><span data-testid="comandas-fechamento-total">{formatCurrency(total)}</span></div>
            </div>
            {precisaForma && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-muted-foreground">Forma de pagamento</div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  {formas.map((f) => (
                    <button key={f} type="button" onClick={() => setForma(f)} aria-pressed={forma === f} className={`flex h-9 items-center justify-center rounded-lg border ${forma === f ? "border-primary bg-primary/10 font-semibold text-primary" : "bg-card text-muted-foreground"}`} data-testid={`comandas-forma-${f}`}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-auto grid gap-2 md:grid-cols-2">
          <Button onClick={() => void enviar("fechar")} disabled={enviando !== null} data-testid="comandas-fechamento-fechar">
            {enviando === "fechar" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {titulo}
          </Button>
          <Button variant="outline" onClick={() => void enviar("registrar")} disabled={enviando !== null || selecionados.length === 0} data-testid="comandas-fechamento-registrar">
            {enviando === "registrar" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Só registrar pagamento
          </Button>
        </div>
      </div>
    </div>
  );
}
